import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Save, User, Calendar, Clock, CheckCircle2, Search, X, Filter, RefreshCw, History, Info } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Select, Skeleton, Modal, Badge } from "@/components/ui-kit";

/**
 * Iter v34b4 (fix #9) — Tambal Absen.
 * Iter v34ch — Riwayat persistent (fetch dari backend) + detail modal.
 * Admin/HR mengisi absensi manual untuk karyawan pada hari yang terlewat.
 */
function fmtDate(s) {
  if (!s) return "-";
  try { return new Date(s).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return s; }
}
function fmtTimeOnly(ts) {
  if (!ts) return "?";
  const m = String(ts).match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : ts;
}

export default function TambalAbsen() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    employee_id: "",
    date: new Date().toISOString().slice(0, 10),
    clock_in: "08:00",
    clock_out: "17:00",
    note: "",
  });
  const [department, setDepartment] = useState("");
  const [departments, setDepartments] = useState([]);

  // Iter v34ch — history state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  // Default filter: 30 hari terakhir
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400 * 1000).toISOString().slice(0, 10);
  const [filter, setFilter] = useState({ start_date: monthAgo, end_date: today, employee_id: "", department: "", q: "" });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = {};
      if (filter.start_date) params.start_date = filter.start_date;
      if (filter.end_date) params.end_date = filter.end_date;
      if (filter.employee_id) params.employee_id = filter.employee_id;
      if (filter.department) params.department = filter.department;
      const r = await api.get("/attendance/manual/history", { params });
      setHistory(r.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal muat riwayat");
    } finally { setHistoryLoading(false); }
  }, [filter.start_date, filter.end_date, filter.employee_id, filter.department]);

  useEffect(() => {
    Promise.all([
      api.get("/employees").catch(() => ({ data: [] })),
      api.get("/department-categories").catch(() => ({ data: [] })),
    ]).then(([e, d]) => {
      setEmployees(e.data || []);
      setDepartments(d.data || []);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const filteredEmployees = useMemo(() => {
    if (!department) return employees;
    return employees.filter((e) => e.department === department);
  }, [employees, department]);

  const displayedHistory = useMemo(() => {
    const q = filter.q.trim().toLowerCase();
    if (!q) return history;
    return history.filter((r) =>
      (r.employee_name || "").toLowerCase().includes(q) ||
      (r.employee_code || "").toLowerCase().includes(q) ||
      (r.manual_note || "").toLowerCase().includes(q) ||
      (r.employee_department || "").toLowerCase().includes(q)
    );
  }, [history, filter.q]);

  const submit = async () => {
    if (!form.employee_id) { toast.error("Pilih karyawan dulu"); return; }
    if (!form.date) { toast.error("Pilih tanggal dulu"); return; }
    if (!form.clock_in) { toast.error("Isi jam masuk"); return; }
    setSaving(true);
    try {
      const emp = employees.find((e) => e.id === form.employee_id);
      await api.post("/attendance/manual", {
        employee_id: form.employee_id,
        date: form.date,
        clock_in_ts: form.clock_in,
        clock_out_ts: form.clock_out || null,
        note: form.note || "",
      });
      toast.success(`Absen tersimpan untuk ${emp?.name} pada ${form.date}`);
      setForm((p) => ({ ...p, employee_id: "", clock_in: "08:00", clock_out: "17:00", note: "" }));
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal simpan");
    } finally { setSaving(false); }
  };

  const openDetail = async (row) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail({ ...row }); // show cached data immediately
    try {
      const r = await api.get(`/attendance/manual/history/${row.id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal muat detail");
    } finally { setDetailLoading(false); }
  };

  return (
    <div className="min-h-full bg-paper p-4 md:p-6 lg:p-8">
      <div className="max-w-[1400px] mx-auto space-y-6">
        <PageHeader
          overline="HR · Absensi"
          title="Tambal Absen"
          description="Isi absensi manual untuk karyawan yang terlewat hari absennya (karena kendala teknis, GPS gagal, dll). Hanya admin/HR yang dapat mengakses."
        />

        {loading ? <Skeleton className="h-96 w-full"/> : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Form */}
            <Card className="lg:col-span-2 p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5"><Calendar className="inline h-3 w-3 mr-1"/> Tanggal</label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="tambal-date"/>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5">Departemen (opsional)</label>
                  <Select value={department} onChange={(e) => setDepartment(e.target.value)} data-testid="tambal-department">
                    <option value="">Semua Departemen</option>
                    {departments.map((d) => <option key={d.id || d.name} value={d.name}>{d.name}</option>)}
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5"><User className="inline h-3 w-3 mr-1"/> Karyawan</label>
                  <Select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} data-testid="tambal-employee">
                    <option value="">-- Pilih Karyawan --</option>
                    {filteredEmployees.map((e) => (
                      <option key={e.id} value={e.id}>{e.name} {e.employee_code ? `(${e.employee_code})` : ""} — {e.department || "-"}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5"><Clock className="inline h-3 w-3 mr-1"/> Jam Masuk</label>
                  <Input type="time" value={form.clock_in} onChange={(e) => setForm({ ...form, clock_in: e.target.value })} data-testid="tambal-clock-in"/>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5"><Clock className="inline h-3 w-3 mr-1"/> Jam Pulang (opsional)</label>
                  <Input type="time" value={form.clock_out} onChange={(e) => setForm({ ...form, clock_out: e.target.value })} data-testid="tambal-clock-out"/>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5">Catatan / Alasan Manual</label>
                  <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="mis: GPS bermasalah, karyawan lupa clock-in, dll" data-testid="tambal-note"/>
                </div>
              </div>
              <div className="pt-3 border-t border-stone-100 flex justify-end gap-2">
                <Button onClick={submit} disabled={saving || !form.employee_id} data-testid="tambal-submit">
                  <Save className="h-3.5 w-3.5"/> {saving ? "Menyimpan..." : "Simpan Absen"}
                </Button>
              </div>
            </Card>

            {/* Recent (persistent) */}
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="h-4 w-4 text-emerald-600"/>
                <h3 className="text-sm font-semibold text-stone-800">Tambal Terbaru</h3>
                <button onClick={loadHistory} className="ml-auto text-stone-400 hover:text-stone-700" data-testid="tambal-refresh-recent">
                  <RefreshCw className="h-3.5 w-3.5"/>
                </button>
              </div>
              {historyLoading ? (
                <div className="space-y-2">{Array.from({length:3}).map((_,i) => <Skeleton key={i} className="h-14"/>)}</div>
              ) : history.length === 0 ? (
                <div className="text-center text-xs text-stone-400 py-6">Belum ada tambal absen dalam 30 hari</div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="tambal-recent">
                  {history.slice(0, 8).map((r) => (
                    <button
                      key={r.id}
                      onClick={() => openDetail(r)}
                      className="w-full text-left border border-stone-100 hover:border-emerald-300 hover:bg-emerald-50/60 rounded-xl p-3 bg-emerald-50/40 transition"
                      data-testid={`tambal-recent-${r.id}`}
                    >
                      <div className="font-medium text-sm text-stone-900">{r.employee_name || "-"}</div>
                      <div className="text-[11px] text-stone-500 mt-0.5 font-mono">{r.date} · {fmtTimeOnly(r.clock_in_ts)} - {fmtTimeOnly(r.clock_out_ts)} ({r.work_hours}h)</div>
                      {r.manual_note && <div className="text-[10px] text-stone-600 mt-1 italic truncate">&ldquo;{r.manual_note}&rdquo;</div>}
                    </button>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Riwayat Tambal Absen (persistent, filterable) */}
        <Card className="p-0">
          <div className="p-4 md:p-5 border-b border-stone-100 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 mr-auto">
              <History className="h-4 w-4 text-blue-700"/>
              <h3 className="text-sm font-semibold text-stone-800">Riwayat Tambal Absen</h3>
              <Badge className="bg-stone-100 text-stone-600 text-[10px]">{displayedHistory.length}</Badge>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1 text-[11px]">
                <Filter className="h-3 w-3 text-stone-500"/>
                <Input type="date" value={filter.start_date} onChange={(e) => setFilter((f) => ({...f, start_date: e.target.value}))} className="!py-1.5 !text-xs !w-36" data-testid="tambal-filter-start"/>
                <span className="text-stone-400">–</span>
                <Input type="date" value={filter.end_date} onChange={(e) => setFilter((f) => ({...f, end_date: e.target.value}))} className="!py-1.5 !text-xs !w-36" data-testid="tambal-filter-end"/>
              </div>
              <Select value={filter.department} onChange={(e) => setFilter((f) => ({...f, department: e.target.value}))} className="!py-1.5 !text-xs !w-40" data-testid="tambal-filter-department">
                <option value="">Semua Departemen</option>
                {departments.map((d) => <option key={d.id || d.name} value={d.name}>{d.name}</option>)}
              </Select>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-stone-400"/>
                <Input value={filter.q} onChange={(e) => setFilter((f) => ({...f, q: e.target.value}))} placeholder="Cari nama / catatan…" className="!py-1.5 !text-xs !pl-7 !w-48" data-testid="tambal-filter-q"/>
              </div>
              {(filter.q || filter.department || filter.employee_id) && (
                <button onClick={() => setFilter((f) => ({...f, q: "", department: "", employee_id: ""}))} className="text-stone-400 hover:text-stone-700" title="Reset filter"><X className="h-3.5 w-3.5"/></button>
              )}
            </div>
          </div>
          {historyLoading ? (
            <div className="p-4 space-y-2">{Array.from({length:5}).map((_,i) => <Skeleton key={i} className="h-12"/>)}</div>
          ) : displayedHistory.length === 0 ? (
            <div className="text-center py-12 text-stone-400 text-sm" data-testid="tambal-history-empty">Tidak ada riwayat sesuai filter.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tambal-history-table">
                <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-widest text-stone-500 border-b border-stone-100">
                  <tr>
                    <th className="py-2.5 px-4">Tanggal</th>
                    <th className="py-2.5 px-4">Karyawan</th>
                    <th className="py-2.5 px-4">Departemen</th>
                    <th className="py-2.5 px-4">Jam</th>
                    <th className="py-2.5 px-4 text-right">Jam Kerja</th>
                    <th className="py-2.5 px-4">Catatan</th>
                    <th className="py-2.5 px-4">Dibuat oleh</th>
                    <th className="py-2.5 px-4 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedHistory.map((r) => (
                    <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50/70" data-testid={`tambal-history-row-${r.id}`}>
                      <td className="py-2 px-4 font-mono">{r.date}</td>
                      <td className="py-2 px-4 font-medium text-stone-900">{r.employee_name || "-"} {r.employee_code && <span className="text-[10px] text-stone-400">({r.employee_code})</span>}</td>
                      <td className="py-2 px-4 text-stone-700">{r.employee_department || "-"}</td>
                      <td className="py-2 px-4 font-mono text-[12px]">{fmtTimeOnly(r.clock_in_ts)} - {fmtTimeOnly(r.clock_out_ts)}</td>
                      <td className="py-2 px-4 text-right font-mono">{r.work_hours || 0}j {r.overtime_hours ? <span className="text-emerald-700 text-[10px]">(+{r.overtime_hours} OT)</span> : null}</td>
                      <td className="py-2 px-4 text-[11px] text-stone-600 max-w-xs truncate" title={r.manual_note}>{r.manual_note || <span className="text-stone-400 italic">-</span>}</td>
                      <td className="py-2 px-4 text-[11px] text-stone-500">{r.created_by || "-"}</td>
                      <td className="py-2 px-4 text-right">
                        <button onClick={() => openDetail(r)} className="text-blue-700 hover:text-blue-900 text-xs font-medium inline-flex items-center gap-1" data-testid={`tambal-history-detail-${r.id}`}>
                          <Info className="h-3.5 w-3.5"/> Detail
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Detail Modal */}
      <Modal open={detailOpen} onClose={() => setDetailOpen(false)} size="lg" title="Detail Tambal Absen">
        {!detail ? (
          <Skeleton className="h-40"/>
        ) : (
          <div className="space-y-4" data-testid="tambal-detail-modal">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Karyawan" value={detail.employee_name || "-"}/>
              <Field label="Kode Karyawan" value={detail.employee_code || "-"} mono/>
              <Field label="Departemen" value={detail.employee_department || "-"}/>
              <Field label="Tanggal" value={detail.date} mono/>
              <Field label="Jam Masuk" value={fmtTimeOnly(detail.clock_in_ts)} mono/>
              <Field label="Jam Pulang" value={fmtTimeOnly(detail.clock_out_ts)} mono/>
              <Field label="Jam Kerja" value={`${detail.work_hours || 0} jam`} mono/>
              <Field label="Lembur" value={`${detail.overtime_hours || 0} jam`} mono/>
              <Field label="Status" value={<StatusPill s={detail.status}/>}/>
              <Field label="Sumber" value={<Badge className="bg-amber-100 text-amber-800 border border-amber-200">Manual / Tambal</Badge>}/>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Catatan / Alasan Manual</p>
              <div className="border border-stone-200 rounded-lg p-3 bg-stone-50 text-sm text-stone-800 min-h-[60px]">{detail.manual_note || <span className="text-stone-400 italic">Tidak ada catatan.</span>}</div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-stone-100">
              <Field label="Dibuat oleh" value={detail.created_by_name ? `${detail.created_by_name} (${detail.created_by})` : (detail.created_by || "-")}/>
              <Field label="Peran" value={detail.created_by_role ? <Badge className="bg-stone-100 text-stone-700">{detail.created_by_role}</Badge> : "-"}/>
              <Field label="Dibuat pada" value={fmtDate(detail.created_at)} mono/>
              <Field label="ID Rekaman" value={detail.id} mono small/>
            </div>
            {detailLoading && <p className="text-xs text-stone-400 italic">Memuat data lengkap…</p>}
          </div>
        )}
      </Modal>
    </div>
  );
}

function Field({ label, value, mono, small }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">{label}</p>
      <p className={`${mono ? "font-mono" : ""} ${small ? "text-[11px]" : "text-sm"} text-stone-800`}>{value}</p>
    </div>
  );
}

function StatusPill({ s }) {
  const map = {
    completed: { c: "bg-emerald-100 text-emerald-800 border-emerald-200", l: "Selesai" },
    in_progress: { c: "bg-blue-100 text-blue-800 border-blue-200", l: "Berlangsung" },
    approved: { c: "bg-sky-100 text-sky-800 border-sky-200", l: "Disetujui" },
    pending_approval: { c: "bg-amber-100 text-amber-800 border-amber-200", l: "Menunggu" },
    rejected: { c: "bg-red-100 text-red-800 border-red-200", l: "Ditolak" },
  };
  const cfg = map[s] || { c: "bg-stone-100 text-stone-700 border-stone-200", l: s || "-" };
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-mono rounded-full border ${cfg.c}`}>{cfg.l}</span>;
}
